import {
  type Component,
  For,
  Match,
  Show,
  Switch,
  createResource,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { fetchMe, fetchReady } from "@/api/client";
import { deleteConversation, listConversations } from "@/api/conversations";
import {
  type AppRoute,
  PRODUCT_LANES,
  chatThreadIdFromPath,
  pathForChatThread,
  pathForRoute,
  routeFromPath,
} from "@/appRoutes";
import { notifyAuthChanged, useAuth } from "@/auth/AuthProvider";
import { authConfigFromEnv, handleCallback } from "@/auth/oidc";
import ChatPage from "@/chat/ChatPage";
import DashboardPage from "@/dashboard/DashboardPage";
import PageBoundary from "@/ui/PageBoundary";
import LandingPage from "@/landing/LandingPage";
import McpSettingsPage from "@/settings/McpSettingsPage";
import AppHeader from "@/ui/AppHeader";
import CommandPalette, {
  type PaletteCommand,
  useCommandPaletteHotkeys,
} from "@/ui/CommandPalette";
import ShortcutsOverlay from "@/ui/ShortcutsOverlay";
import { AuthOverlay } from "@/ui/chrome";
import VmsPage from "@/vms/VmsPage";

const App: Component = () => {
  const auth = useAuth();
  const [me] = createResource(
    () => (auth.isAuthenticated() ? "fetch" : null),
    () => fetchMe(),
  );
  // One global health pill in the TopStrip — replaces the three
  // placeholder stats. Polled lightly because readyz is the canonical
  // up/down indicator the user sees on every authenticated page.
  const [ready] = createResource(
    () => (auth.isAuthenticated() ? "fetch" : null),
    () => fetchReady(),
  );
  // Persisted threads for ⌘K palette search. Refreshed on auth flips
  // AND after each completed chat turn — ChatPage calls
  // `refetchPaletteThreads` via the prop below so the palette reflects
  // the new/just-updated conversation without a page reload.
  const [paletteThreads, { refetch: refetchPaletteThreads }] = createResource(
    () => (auth.isAuthenticated() ? "fetch" : null),
    () => listConversations().catch(() => []),
  );
  const [callbackError, setCallbackError] = createSignal<string | null>(null);
  const [callbackBusy, setCallbackBusy] = createSignal(false);
  const [route, setRoute] = createSignal<AppRoute>(
    routeFromPath(window.location.pathname),
  );
  const [chatThreadId, setChatThreadId] = createSignal<string | null>(
    chatThreadIdFromPath(window.location.pathname),
  );

  const navigate = (next: AppRoute) => {
    const path = pathForRoute(next);
    window.history.pushState({}, "", path);
    setRoute(routeFromPath(path));
    setChatThreadId(chatThreadIdFromPath(path));
  };

  const navigateToChatThread = (threadId: string | null) => {
    const path = threadId ? pathForChatThread(threadId) : "/chat";
    window.history.pushState({}, "", path);
    setRoute(routeFromPath(path));
    setChatThreadId(threadId);
  };

  // /auth/callback is special: exchange tokens first, then render the
  // stored return_to route. Production deep-link fallback is Phase 5.
  onMount(async () => {
    const syncRoute = () => {
      setRoute(routeFromPath(window.location.pathname));
      setChatThreadId(chatThreadIdFromPath(window.location.pathname));
    };
    window.addEventListener("popstate", syncRoute);
    onCleanup(() => window.removeEventListener("popstate", syncRoute));

    if (window.location.pathname !== "/auth/callback") return;
    setCallbackBusy(true);
    try {
      const result = await handleCallback(authConfigFromEnv());
      notifyAuthChanged();
      const returnTo = result.return_to || "/chat";
      window.history.replaceState({}, "", returnTo);
      setRoute(routeFromPath(returnTo));
    } catch (err) {
      setCallbackError(err instanceof Error ? err.message : String(err));
    } finally {
      setCallbackBusy(false);
    }
  });

  const login = () => {
    void auth.login();
  };

  const [paletteOpen, setPaletteOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);
  useCommandPaletteHotkeys(setPaletteOpen, setShortcutsOpen);

  // Commands are rebuilt on every render so they close over fresh
  // signals. The list is small; recomputing is cheap.
  const commands = (): PaletteCommand[] => {
    const authed = auth.isAuthenticated();
    const base: PaletteCommand[] = [];
    if (authed) {
      base.push(
        {
          id: "go-chat",
          label: "Open chat",
          hint: "inference + tools",
          keywords: "llm model gpt",
          run: () => navigate("chat"),
        },
        {
          id: "go-dashboard",
          label: "Open dashboard",
          hint: "operator console",
          keywords: "home overview",
          run: () => navigate("dashboard"),
        },
        {
          id: "new-thread",
          label: "New chat thread",
          hint: "blank conversation",
          keywords: "create fresh",
          run: () => navigateToChatThread(null),
        },
        {
          id: "go-compute",
          label: "Open compute",
          hint: "VMs + sandbox",
          keywords: "gpu vm launch",
          run: () => navigate("compute"),
        },
        {
          id: "go-cli",
          label: "Open CLI lane",
          hint: "z01-cli login path",
          keywords: "terminal shell",
          run: () => navigate("cli"),
        },
        {
          id: "go-settings",
          label: "Open settings",
          hint: "MCP servers + access",
          keywords: "mcp tools preferences",
          run: () => navigate("settings"),
        },
        {
          id: "inspect-log",
          label: "Inspect log",
          hint: "open browser console",
          keywords: "debug devtools error",
          run: () => {
            // eslint-disable-next-line no-console
            console.info(
              "[z01] Use your browser devtools (F12 / ⌥⌘I) to inspect logs.",
            );
          },
        },
        {
          id: "sign-out",
          label: "Sign out",
          hint: "end this session",
          keywords: "logout exit",
          run: () => auth.logout(),
        },
      );
      // Dynamic per-thread entries — typing a thread title in the
      // palette surfaces it as a command that navigates to /chat/<id>.
      const threads = paletteThreads();
      if (threads) {
        for (const t of threads) {
          base.push({
            id: `thread-${t.id}`,
            label: t.title || "(untitled thread)",
            hint: "chat thread",
            keywords: `thread conversation ${t.id}`,
            run: () => navigateToChatThread(t.id),
            onDelete: async () => {
              try {
                await deleteConversation(t.id);
              } finally {
                // If we just deleted the thread that's open, drop back to a
                // fresh chat; either way refresh the palette list.
                if (chatThreadId() === t.id) navigateToChatThread(null);
                void refetchPaletteThreads();
              }
            },
          });
        }
      }
    } else {
      base.push({
        id: "sign-in",
        label: "Sign in with ZITADEL",
        hint: "open OIDC flow",
        keywords: "login auth",
        run: login,
      });
    }
    return base;
  };

  const userSummary = () => {
    if (!auth.isAuthenticated()) return null;
    const m = me();
    return {
      email: m?.user.email ?? "loading account…",
      role: m?.roles[0] ?? "user",
      onLogout: () => auth.logout(),
    };
  };

  // Logged-out root = public landing.
  // Logged-in root = chat surface per CTO direction (2026-05-26).
  if (route() === "home" && !auth.isAuthenticated()) {
    return (
      <>
        <LandingPage onLogin={login} />
        <AuthOverlay on={callbackBusy()} direction="in" />
      </>
    );
  }
  const effectiveRoute = (): AppRoute =>
    route() === "home" && auth.isAuthenticated() ? "chat" : route();

  return (
    <>
      <div class="app-viewport">
        <AppHeader
          currentRoute={effectiveRoute()}
          authenticated={auth.isAuthenticated()}
          onNavigate={navigate}
          onOpenPalette={() => setPaletteOpen(true)}
          onLogout={auth.isAuthenticated() ? () => auth.logout() : undefined}
        />
        <div class="app-scroll">
          <Show when={callbackError()}>
            {(msg) => (
              <div class="neuro-page">
                <div class="neuro-surface">
                  <div class="neuro-route-body">
                    <h2>sign-in failed</h2>
                    <p>{msg()}</p>
                  </div>
                </div>
              </div>
            )}
          </Show>

          <Switch>
            <Match when={effectiveRoute() === "dashboard"}>
              <PageBoundary>
                <Show
                  when={auth.isAuthenticated()}
                  fallback={<AuthPrompt onLogin={login} />}
                >
                  <DashboardPage navigate={navigate} />
                </Show>
              </PageBoundary>
            </Match>
            <Match when={effectiveRoute() === "chat"}>
              <PageBoundary>
                <Show
                  when={auth.isAuthenticated()}
                  fallback={<AuthPrompt onLogin={login} />}
                >
                  <ChatPage
                    threadId={chatThreadId()}
                    navigateToThread={navigateToChatThread}
                    onTurnSettled={() => void refetchPaletteThreads()}
                    overlayOpen={() => paletteOpen() || shortcutsOpen()}
                  />
                </Show>
              </PageBoundary>
            </Match>
            <Match when={effectiveRoute() === "compute"}>
              <PageBoundary>
                <Show
                  when={auth.isAuthenticated()}
                  fallback={<AuthPrompt onLogin={login} />}
                >
                  <VmsPage />
                </Show>
              </PageBoundary>
            </Match>
            <Match when={effectiveRoute() === "cli"}>
              <PageBoundary>
                <CliLane
                  authenticated={auth.isAuthenticated()}
                  onLogin={login}
                />
              </PageBoundary>
            </Match>
            <Match when={effectiveRoute() === "settings"}>
              <PageBoundary>
                <Show
                  when={auth.isAuthenticated()}
                  fallback={<AuthPrompt onLogin={login} />}
                >
                  <McpSettingsPage />
                </Show>
              </PageBoundary>
            </Match>
            <Match when={effectiveRoute() === "not-found"}>
              <div class="neuro-page">
                <div class="neuro-surface">
                  <div class="neuro-route-body">
                    <h2>route not found</h2>
                    <p>that lane does not exist yet.</p>
                    <button
                      type="button"
                      class="neuro-action"
                      onClick={() => navigate("chat")}
                    >
                      open chat
                    </button>
                  </div>
                </div>
              </div>
            </Match>
          </Switch>
        </div>
      </div>
      <AuthOverlay on={callbackBusy()} direction="in" />
      <CommandPalette
        open={paletteOpen()}
        commands={commands()}
        onClose={() => setPaletteOpen(false)}
      />
      <ShortcutsOverlay
        open={shortcutsOpen()}
        onClose={() => setShortcutsOpen(false)}
      />
    </>
  );
};

const AuthPrompt: Component<{
  onLogin: () => void;
}> = (props) => (
  <div class="neuro-page">
    <div class="neuro-surface">
      <div class="neuro-route-body">
        <h2>sign in to continue</h2>
        <p>tenant-scoped routes require an account.</p>
        <button type="button" class="neuro-action" onClick={props.onLogin}>
          sign in with ZITADEL
        </button>
      </div>
    </div>
  </div>
);

const CliLane: Component<{ authenticated: boolean; onLogin: () => void }> = (
  props,
) => (
  <div class="neuro-page">
    <div class="neuro-surface">
      <div class="neuro-route-body">
        <h2>CLI</h2>
        <p>
          z01-cli is a Rust binary that authenticates against z01.ai via OAuth
          device flow. Token lives in your OS keyring; refresh is automatic.
        </p>

        <h3>Install</h3>
        <pre>
          cargo install --git https://github.com/zeesthoughts/z01-platform
          z01-cli
        </pre>
        <p>
          binary lands at <code>~/.cargo/bin/z01-cli</code>.
        </p>

        <h3>Authenticate</h3>
        <pre>z01-cli login</pre>
        <p>
          prints a device code and opens auth.z01.ai. Approve in browser; token
          is stored in OS keyring.
        </p>
        <pre>z01-cli status</pre>
        <p>shows sub / expiry / refresh-token presence.</p>
        <pre>z01-cli logout</pre>
        <p>clears the keyring entry.</p>

        <h3>Account</h3>
        <Show
          when={props.authenticated}
          fallback={
            <>
              <p>not signed in — CLI signs in with this web account.</p>
              <button
                type="button"
                class="neuro-action"
                onClick={props.onLogin}
              >
                sign in with ZITADEL
              </button>
            </>
          }
        >
          <p>account ready — CLI signs in with this identity.</p>
        </Show>
      </div>
    </div>
  </div>
);

export default App;
